import { memo, useEffect } from 'react';
import { Chart } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    type ChartOptions,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';

import { cleanupCustomTooltip, createCustomTooltip } from '@/shared/lib/chart';
import { useTheme } from '@/shared/lib/hooks';
import { roundToDecimals } from '@/shared/lib/math';

import type { AdaptedHistogramChartData } from '../../model/types/chart';

import cls from './pattern-bar-chart.module.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
);

interface PatternBarChartProps {
    data: AdaptedHistogramChartData;
    title: string;
    height?: number;
}

/**
 * Bar chart component where each bar represents a single pattern occurrence.
 * X-axis: Occurrence index (0, 1, 2, 3, ...)
 * Y-axis: Number of candles to reach target/failure for that occurrence
 */
export const PatternBarChart = memo(
    ({ data, title, height = 200 }: PatternBarChartProps) => {
        const { t } = useTranslation('pattern-studio');
        const isDarkTheme = useTheme()[2];

        useEffect(
            () => () => {
                cleanupCustomTooltip();
            },
            [],
        );

        if (!data?.data || !Array.isArray(data.data)) {
            return (
                <div className={cls.noData}>
                    <p>{t('chart.noData')}</p>
                </div>
            );
        }

        const validData = data.data.filter(
            (value) => value !== null && value !== undefined,
        ) as number[];

        if (validData.length === 0) {
            return (
                <div className={cls.noData}>
                    <p>{t('chart.noValidData')}</p>
                </div>
            );
        }

        // Create labels for each occurrence (0, 1, 2, 3, ...)
        const labels = validData.map((_, index) => `${index}`);

        // Calculate average
        const average = validData.reduce((a, b) => a + b, 0) / validData.length;

        // Create average line data (same value for all points)
        const averageLineData = new Array(validData.length).fill(average);

        // Chart constants
        const BAR_TYPE = 'bar' as const;
        const LINE_TYPE = 'line' as const;
        const TRANSPARENT_COLOR = 'transparent';
        const AVERAGE_LABEL = t('chart.average');

        const chartData = {
            labels,
            datasets: [
                {
                    label: title,
                    data: validData,
                    backgroundColor: 'rgba(24, 144, 255, 0.8)',
                    borderColor: 'rgba(24, 144, 255, 1)',
                    borderWidth: 1,
                    borderRadius: 4,
                    type: BAR_TYPE,
                },
                {
                    label: AVERAGE_LABEL,
                    data: averageLineData,
                    borderColor: '#52c41a',
                    backgroundColor: TRANSPARENT_COLOR,
                    borderWidth: 2,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    type: LINE_TYPE,
                    yAxisID: 'y',
                    borderDash: [5, 5],
                },
            ],
        };

        const chartOptions: ChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                title: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                    external: createCustomTooltip({
                        isDark: isDarkTheme,
                        title,
                        formatItems: (tooltipModel) =>
                            tooltipModel.dataPoints.map((point) => {
                                const value = point.raw as number;
                                const rounded = roundToDecimals(value);
                                const isAverage = point.datasetIndex === 1;

                                return {
                                    color: point.dataset.borderColor as string,
                                    label: isAverage
                                        ? AVERAGE_LABEL
                                        : `Position ${point.dataIndex}`,
                                    value: `${rounded} candles`,
                                };
                            }),
                    }),
                },
            },
            scales: {
                x: {
                    grid: {
                        display: true,
                        color: isDarkTheme
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.1)',
                        drawTicks: false,
                    },
                    title: {
                        display: true,
                        text: '',
                    },
                },
                y: {
                    beginAtZero: true,
                    grid: {
                        display: true,
                        color: isDarkTheme
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.1)',
                        drawTicks: false,
                    },
                    title: {
                        display: true,
                        text: 'Number of Candles',
                    },
                },
            },
        };

        return (
            <div className={cls.container}>
                <div
                    className={cls.chart}
                    style={{ height: `${height}px` }}
                >
                    <Chart
                        data={chartData}
                        options={chartOptions}
                        type="bar"
                    />
                </div>
            </div>
        );
    },
);

PatternBarChart.displayName = 'PatternBarChart';
